import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Button } from '../../../../../../../components/atoms/button/button';
import { Drawer } from '../../../../../../../components/molecules/drawer/drawer';
import {
  ClienteTaxa,
  TEMPO_RECEBIMENTO_LABEL,
  TEMPO_RECEBIMENTO_ORDER,
  TempoRecebimento,
} from '../../../../../../../types/cliente-taxa';
import { ConfirmarTaxasModal } from './confirmar-taxas-modal';
import { TaxaFormTab } from './taxa-form-tab';
import { hasErrors, TaxaFieldErrors, validateTaxa } from './validation';

interface Props {
  isOpen: boolean;
  clienteNome: string;
  taxas: ClienteTaxa[];
  saving?: boolean;
  errorMessage?: string | null;
  onSave: (taxas: ClienteTaxa[]) => Promise<unknown> | void;
  onClose: () => void;
}

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
`;

const TabButton = styled.button<{ $active: boolean }>`
  background: transparent;
  border: none;
  border-bottom: 2px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.brandPrimary : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  padding: 10px 18px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ErrorAlert = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.dangerBg};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
`;

const ALL_FIELDS_VALID = (errors: Record<TempoRecebimento, TaxaFieldErrors>) =>
  Object.values(errors).every((e) => !hasErrors(e));

const cloneTaxa = (t: ClienteTaxa): ClienteTaxa => ({ ...t });

export const EditarTaxasDrawer = ({
  isOpen,
  clienteNome,
  taxas,
  saving,
  errorMessage,
  onSave,
  onClose,
}: Props) => {
  const [snapshot, setSnapshot] = useState<ClienteTaxa[]>([]);
  const [values, setValues] = useState<Record<TempoRecebimento, ClienteTaxa>>(
    {} as Record<TempoRecebimento, ClienteTaxa>,
  );
  const [errors, setErrors] = useState<
    Record<TempoRecebimento, TaxaFieldErrors>
  >({} as Record<TempoRecebimento, TaxaFieldErrors>);
  const [activeTab, setActiveTab] = useState<TempoRecebimento>(
    TempoRecebimento.D_30,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const map: Record<TempoRecebimento, ClienteTaxa> = {} as never;
    const errMap: Record<TempoRecebimento, TaxaFieldErrors> = {} as never;
    for (const t of taxas) {
      map[t.recebimento] = cloneTaxa(t);
      errMap[t.recebimento] = {};
    }
    setSnapshot(taxas.map(cloneTaxa));
    setValues(map);
    setErrors(errMap);
    setActiveTab(TempoRecebimento.D_30);
    setConfirmOpen(false);
  }, [isOpen, taxas]);

  const orderedRecebimentos = useMemo(
    () => TEMPO_RECEBIMENTO_ORDER.filter((r) => values[r] != null),
    [values],
  );

  const isDirty = useMemo(() => {
    return snapshot.some((before) => {
      const after = values[before.recebimento];
      if (!after) return false;
      return Object.keys(before).some(
        (k) =>
          (before as unknown as Record<string, unknown>)[k] !==
          (after as unknown as Record<string, unknown>)[k],
      );
    });
  }, [snapshot, values]);

  const allValid = ALL_FIELDS_VALID(errors);

  const handleFieldChange = (
    recebimento: TempoRecebimento,
    field: keyof ClienteTaxa,
    value: number | '',
  ) => {
    setValues((prev) => {
      const next = { ...prev };
      next[recebimento] = {
        ...next[recebimento],
        [field]: value as unknown as number,
      };
      return next;
    });
    setErrors((prev) => {
      const nextValues = {
        ...values[recebimento],
        [field]: value as unknown as number,
      };
      return { ...prev, [recebimento]: validateTaxa(nextValues) };
    });
  };

  const orderedAfter: ClienteTaxa[] = orderedRecebimentos.map((r) => values[r]);

  const handleSaveClick = () => {
    if (!isDirty || !allValid) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    await onSave(orderedAfter);
  };

  if (!isOpen) return null;

  const activeValues = values[activeTab];
  const activeErrors = errors[activeTab] ?? {};

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={saving ? () => undefined : onClose}
        width={720}
        title="Editar taxas comerciais"
        description={`Cliente: ${clienteNome}`}
        footer={
          <Footer>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveClick}
              disabled={!isDirty || !allValid}
              loading={saving}
              data-testid="editar-taxas-salvar"
            >
              Salvar
            </Button>
          </Footer>
        }
      >
        {errorMessage && <ErrorAlert>{errorMessage}</ErrorAlert>}

        <Tabs role="tablist" aria-label="Tipos de recebimento">
          {orderedRecebimentos.map((r) => (
            <TabButton
              key={r}
              role="tab"
              aria-selected={activeTab === r}
              data-testid={`editar-taxas-tab-${r}`}
              $active={activeTab === r}
              onClick={() => setActiveTab(r)}
            >
              {TEMPO_RECEBIMENTO_LABEL[r]}
            </TabButton>
          ))}
        </Tabs>

        {activeValues && (
          <TaxaFormTab
            values={activeValues}
            errors={activeErrors}
            onChange={(field, value) =>
              handleFieldChange(activeTab, field, value)
            }
          />
        )}
      </Drawer>

      <ConfirmarTaxasModal
        isOpen={confirmOpen}
        clienteNome={clienteNome}
        before={snapshot}
        after={orderedAfter}
        saving={saving}
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};
